import { useState, useCallback } from "react";

import { StickyNote, Check, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/modern/utils";
import { Button } from "@/modern/components/ui/button-shadcn";
import { updateNotes } from "@/network/network";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { useEntityManagerContext } from "@/react/components/entityManager/context/EntityManagerContext";
import { WidgetParamKeys } from "@/types/entity";

import { useFileDetailModal } from "../../../context/FileDetailModalContext";
import { ExtractedNote } from "../types";

interface NoteCardState {
  created: boolean;
  processing: boolean;
  expanded: boolean;
}

interface NotesExtractionResultsProps {
  notes: ExtractedNote[];
}

export const NotesExtractionResults = ({ notes }: NotesExtractionResultsProps) => {
  const { project, entityRecord, file } = useFileDetailModal();
  const { copyEntityLink } = useEntityManagerContext();

  const [cardStates, setCardStates] = useState<Record<number, NoteCardState>>({});

  const getCardState = (index: number): NoteCardState =>
    cardStates[index] || { created: false, processing: false, expanded: false };

  const updateCardState = (index: number, updates: Partial<NoteCardState>) => {
    setCardStates((prev) => ({
      ...prev,
      [index]: { ...getCardState(index), ...updates },
    }));
  };

  const handleCreateNote = useCallback(
    async (note: ExtractedNote, index: number) => {
      if (!project || !entityRecord) return;

      const state = getCardState(index);
      if (state.created || state.processing) return;

      updateCardState(index, { processing: true });

      try {
        const boardNotesStore = project.boardNotesStore;
        if (!boardNotesStore) {
          throw new Error("Board notes store not available");
        }

        const noteName = note.name || "Extracted Note";

        const noteRecord = boardNotesStore.createRecord({
          [entityRecord.entityType]: entityRecord.id,
          name: noteName,
          description: note.content,
        });

        const selectedBoard = getSavedBoardSelection();
        const boardId = selectedBoard?.id ? Number(selectedBoard.id) : null;

        await new Promise<void>((resolve, reject) => {
          const detacher = boardNotesStore.on({
            async idChange({ record }: { record: any }) {
              if (record.id === noteRecord.id) {
                try {
                  let sourceTag = "";
                  if (file?.id) {
                    const fileLink = await copyEntityLink(
                      WidgetParamKeys.FILE.EDIT,
                      file.id,
                      undefined,
                      true
                    );
                    const fileName = file.name || "document";
                    sourceTag = `<p><br></p><p><em>Extracted from: <a href="${fileLink}" target="_blank" rel="noopener noreferrer">${fileName}</a></em></p>`;
                  }
                  const htmlContent = `${note.content}${sourceTag}`;

                  await updateNotes(noteRecord.id, {
                    content: htmlContent,
                    board: boardId,
                    archived: false,
                  });
                  resolve();
                } catch (err) {
                  reject(err);
                } finally {
                  detacher();
                }
              }
            },
            once: true,
          });

          boardNotesStore.add(noteRecord);
        });

        updateCardState(index, { created: true, processing: false });
        toast.success("Note Created", {
          description: `"${noteName}" has been saved as a Swivu Note`,
        });
      } catch (error) {
        console.error("[NotesExtractionResults] Failed to create note:", error);
        updateCardState(index, { processing: false });
        toast.error("Failed to Create Note", {
          description: "Something went wrong. Please try again.",
        });
      }
    },
    [project, entityRecord, file, copyEntityLink, cardStates]
  );

  return (
    <div className="flex flex-col gap-3 p-3">
      {notes.map((note, index) => {
        const state = getCardState(index);

        return (
          <div
            key={index}
            className={cn(
              "border border-border rounded-swivu-base overflow-hidden",
              state.created && "border-green-500/30 bg-green-500/5"
            )}
          >
            {/* Header */}
            <button
              type="button"
              onClick={() => updateCardState(index, { expanded: !state.expanded })}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/50 transition-colors"
            >
              {state.expanded ? (
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              )}
              <StickyNote className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <span className="text-sm font-medium text-foreground truncate flex-1">
                {note.name || `Note ${index + 1}`}
              </span>
              {state.created && (
                <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400 shrink-0" />
              )}
            </button>

            {/* Expandable content */}
            {state.expanded && (
              <div className="border-t border-border">
                <div
                  className="px-3 py-2 text-sm text-foreground prose prose-sm max-w-none dark:prose-invert"
                  dangerouslySetInnerHTML={{ __html: note.content }}
                />

                <div className="px-3 pb-2">
                  <Button
                    type="button"
                    variant={state.created ? "ghost" : "default"}
                    size="sm"
                    disabled={state.created || state.processing}
                    onClick={() => handleCreateNote(note, index)}
                    className={cn(
                      "w-full gap-2",
                      state.created && "text-green-600 dark:text-green-400"
                    )}
                  >
                    {state.created ? (
                      <>
                        <Check className="w-4 h-4" />
                        Note Created
                      </>
                    ) : (
                      <>
                        <StickyNote className="w-4 h-4" />
                        {state.processing ? "Creating..." : "Create Swivu Note"}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
